import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { useTheme } from 'styled-components';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import 'flatpickr/dist/themes/dark.css';

import CalendarIcon from '@/core/components/Icons/svg/calendar.svg';
import { LocationSearch } from '@/core/components/LocationSearch';
import { SelectComponent } from '@/core/components/Select';
import { SelectOptionType } from '@/core/components/Select/SelectComponent.types';
import { TimePicker } from '@/core/components/TimePicker';
import { impactFilterOptions } from '@/core/constants/filters';
import { ReportImpact } from '@/core/interfaces/common';
import {
  FlatpickrCalendarContainer,
  FlatpickrInputIcon,
  FlatpickrInputWrapper,
} from '@/core/styles/flatpickr.styles';
import {
  getCategoriesSelector,
  getCountriesWithWatersSelector,
} from '@/core/store/reducers/config';

import { Label, MetadataField, MetadataGrid } from '@/features/EditAnalysis/EditAnalysis.styles';

import type { EditAnalysisFormData } from '../../interfaces';

export const MetadataSection = () => {
  const { control } = useFormContext<EditAnalysisFormData>();
  const theme = useTheme();
  const categoriesList = useSelector(getCategoriesSelector);
  const countriesList = useSelector(getCountriesWithWatersSelector);
  const [calendarContainer, setCalendarContainer] = useState<HTMLDivElement | null>(null);

  const categoryOptions = useMemo(
    () =>
      categoriesList.map(cat => ({
        label: cat.name,
        value: cat.id,
      })),
    [categoriesList]
  );

  const countryOptions = useMemo<Array<SelectOptionType<number>>>(
    () =>
      countriesList.map(c => ({
        label: c.name,
        value: c.id,
      })),
    [countriesList]
  );

  const appendTo = calendarContainer ?? undefined;
  const isDark = theme.theme === 'dark';
  const flatpickrTheme = isDark ? 'dark' : 'light';

  const pickerDateConfig = useMemo(
    () => ({
      dateFormat: 'Y-m-d',
      allowInput: false,
      appendTo,
      theme: flatpickrTheme,
    }),
    [appendTo, flatpickrTheme]
  );

  const pickerKey = useMemo(
    () => `flatpickr-${flatpickrTheme}-${appendTo ? 'in' : 'out'}`,
    [flatpickrTheme, appendTo]
  );

  return (
    <MetadataGrid>
      <FlatpickrCalendarContainer
        ref={setCalendarContainer}
        aria-hidden
      />
      <MetadataField>
        <Controller
          control={control}
          name="location"
          render={({ field: locField }) => (
            <Controller
              control={control}
              name="locationCoordinates"
              render={({ field: coordField }) => (
                <LocationSearch
                  label="Location"
                  placeholder="e.g. City, region or address"
                  location={
                    locField.value || coordField.value
                      ? {
                          name: locField.value,
                          coordinates: coordField.value ?? undefined,
                        }
                      : undefined
                  }
                  onSelectLocation={suggestion => {
                    locField.onChange(suggestion.name);
                    coordField.onChange(
                      suggestion.coordinates
                        ? {
                            lat: suggestion.coordinates.lat,
                            lng: suggestion.coordinates.lng,
                          }
                        : null
                    );
                  }}
                />
              )}
            />
          )}
        />
      </MetadataField>
      <MetadataField>
        <Controller
          control={control}
          name="countryIds"
          render={({ field }) => (
            <SelectComponent
              label="Country"
              isSearchable
              placeholder="Select country"
              options={countryOptions}
              value={
                field.value.length > 0
                  ? countryOptions.find(opt => opt.value === field.value[0]) ?? undefined
                  : undefined
              }
              onChange={opt => field.onChange(opt ? [opt.value] : [])}
            />
          )}
        />
      </MetadataField>
      <MetadataField>
        <Label>Start date</Label>
        <Controller
          control={control}
          name="eventDate"
          render={({ field }) => (
            <FlatpickrInputWrapper $withIcon>
              <Flatpickr
                key={pickerKey}
                value={field.value || undefined}
                onChange={(_dates: Array<Date>, dateStr: string) => field.onChange(dateStr)}
                options={pickerDateConfig}
                placeholder="dd/mm/yyyy"
              />
              <FlatpickrInputIcon>
                <CalendarIcon
                  width={18}
                  height={18}
                />
              </FlatpickrInputIcon>
            </FlatpickrInputWrapper>
          )}
        />
      </MetadataField>
      <MetadataField>
        <Label>End date</Label>
        <Controller
          control={control}
          name="eventEndDate"
          render={({ field }) => (
            <FlatpickrInputWrapper $withIcon>
              <Flatpickr
                key={pickerKey}
                value={field.value || undefined}
                onChange={(_dates: Array<Date>, dateStr: string) => field.onChange(dateStr)}
                options={pickerDateConfig}
                placeholder="dd/mm/yyyy"
              />
              <FlatpickrInputIcon>
                <CalendarIcon
                  width={18}
                  height={18}
                />
              </FlatpickrInputIcon>
            </FlatpickrInputWrapper>
          )}
        />
      </MetadataField>
      <MetadataField>
        <Label>Start time</Label>
        <Controller
          control={control}
          name="eventTime"
          render={({ field }) => (
            <TimePicker
              value={field.value ?? ''}
              onChange={field.onChange}
              placeholder="--:--"
            />
          )}
        />
      </MetadataField>
      <MetadataField>
        <Label>End time</Label>
        <Controller
          control={control}
          name="eventEndTime"
          render={({ field }) => (
            <TimePicker
              value={field.value ?? ''}
              onChange={field.onChange}
              placeholder="--:--"
            />
          )}
        />
      </MetadataField>
      <MetadataField $span={2}>
        <Controller
          control={control}
          name="impact"
          render={({ field }) => (
            <SelectComponent
              label="Impact"
              placeholder="Select impact"
              options={impactFilterOptions}
              value={impactFilterOptions.find(opt => opt.value === field.value) ?? undefined}
              onChange={opt => field.onChange(opt?.value ?? ReportImpact.EXTREME)}
            />
          )}
        />
      </MetadataField>
      <MetadataField $span={2}>
        <Controller
          control={control}
          name="categoryIds"
          render={({ field }) => (
            <SelectComponent
              label="Category"
              isMulti
              isSearchable
              placeholder="Select categories"
              options={categoryOptions}
              value={categoryOptions.filter(opt => field.value.includes(opt.value))}
              onChange={opts => field.onChange((opts ?? []).map(o => o.value))}
            />
          )}
        />
      </MetadataField>
    </MetadataGrid>
  );
};
